import { useEffect, useMemo, useState } from 'react';

import { Option } from 'common/components/Inputs/Autocomplete/utils/models';
import { Tag } from 'tags/models/tags';
import { useTagsByIds } from './useTagsByIds';
import { useInfinityTagsQuery } from './useInfinityTagsQuery';
import { useSaveOnMountArrayState } from 'common/hooks/useSaveOnMountArrayState.ts';

type UsTagsAutocompleteProps = {
  initialIds: string[];
  hints: string[];
  search: string;
  onSelectedChange: (options: Option[]) => void;
};

const mapTagsToOption = (tags: Tag[]) => {
  return tags.map((tag) => ({
    id: tag.id.toString(),
    name: tag.name,
  }));
};

export const useTagsAutocomplete = ({
  search,
  onSelectedChange,
  initialIds,
  hints,
}: UsTagsAutocompleteProps) => {
  const displayHints = !search;

  const initialSelectedIds = useSaveOnMountArrayState(initialIds);

  const {
    selectedTags: initialSelectedTags,
    isPending: isInitialFetching,
    isFetched: isInitialFetched,
  } = useTagsByIds(initialSelectedIds);

  const [selectedOptions, setSelectedOptions] = useState<Option[]>([]);
  if (initialIds.length === 0 && selectedOptions.length !== 0) {
    setSelectedOptions([]);
  }

  useEffect(() => {
    if (isInitialFetched) {
      setSelectedOptions(mapTagsToOption(initialSelectedTags));
    }
  }, [initialSelectedTags, isInitialFetched]);

  const updateSelectedOptions = (options: Option[]) => {
    setSelectedOptions(options);
    onSelectedChange(options);
  };

  const {
    tags: tagsData,
    isFetched,
    ...queryProps
  } = useInfinityTagsQuery(search, {
    minTime: 250,
  });

  const { selectedTags: hintedTags, isFetched: isHintFetched } = useTagsByIds(hints);

  const hintOptions = useMemo(
    () => (displayHints ? mapTagsToOption(hintedTags) : []),
    [hintedTags, displayHints],
  );

  const options = useMemo(() => mapTagsToOption(tagsData), [tagsData]);

  return {
    selectedOptions,
    isSelectedOptionFetching: isInitialFetching,
    hintOptions,
    updateSelectedOptions,
    options: options,
    isFetched: isFetched && isHintFetched,
    ...queryProps,
  };
};
